import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useCallContext } from "../../../store/context/CallProvider";
import { useSocket } from "../../../store/context/SocketProvider";
import sound from "../../../assets/sounds/Ringing.mp3";

const CallModal = React.memo((props) => {
  const {
    isOpen,
    toggleCallModal,
    isVideo,
    user,
    inComming,
    currentUser,
    conversationId,
    updateMessages,
  } = props;

  const audioRef = useRef(null);
  const { callUser, declineCall, answerCall } = useCallContext();
  const { socket } = useSocket();

  const userDisplay = useMemo(() => ({
    name: user?.isGroupChat
      ? user.name
      : `${user?.contactName?.first ?? ""} ${user?.contactName?.last ?? ""}`.trim(),
    image: user?.isGroupChat
      ? "https://static.vecteezy.com/system/resources/thumbnails/021/063/599/small/group-coaching-icon-vector.jpg"
      : user?.imageSrc,
  }), [user]);

  const controlAudio = useCallback((action = "stop") => {
    if (!audioRef.current) return;
    if (action === "play") {
      audioRef.current.volume = inComming ? 1 : 0.3;
      audioRef.current.loop = true;
      audioRef.current.play();
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [inComming]);

  const handleDecline = useCallback(() => {
    controlAudio("stop");
    declineCall();
    toggleCallModal();
  }, [controlAudio, declineCall, toggleCallModal]);

  const handleAnswer = useCallback(() => {
    controlAudio("stop");
    answerCall();
  }, [answerCall, controlAudio]);

  const handleCall = useCallback((type) => {
    controlAudio("play");
    callUser(user, type === "video", currentUser, conversationId);
  }, [callUser, controlAudio, conversationId, currentUser, user]);

  useEffect(() => {
    audioRef.current = new Audio(sound);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (inComming && isOpen) controlAudio("play");
    else controlAudio("stop");
  }, [inComming, isOpen, controlAudio]);

  const callTypeLabel = isVideo ? "Video Call" : "Audio Call";
  const callStatus = useMemo(() => {
    if (inComming) return "Calling you...";
    return isVideo ? "Start Video Call" : "Start Audio Call";
  }, [inComming, isVideo]);

  if (!isOpen) return null;

  return (
    <div className="ct-toast" role="dialog" aria-label="Call notification">

      <div className="ct-header">
        <div className="ct-header-left">
          <span className="ct-badge">{inComming ? "Incoming" : "Outgoing"}</span>
          <p className="ct-header-title">{callTypeLabel}</p>
        </div>
        <button className="ct-dismiss" onClick={handleDecline} aria-label="Dismiss">
          &#x2715;
        </button>
      </div>

      <div className="ct-body">
        <div className="ct-identity">
          <div className="ct-avatar-wrap">
            <img className="ct-avatar" src={userDisplay.image} alt={userDisplay.name} />
            <div className="ct-avatar-ring" />
            <div className="ct-type-badge">
              <i className={`ri-${isVideo ? "vidicon" : "phone"}-fill`} style={{ fontSize: 9 }} />
            </div>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p className="ct-name">{userDisplay.name}</p>
            <p className="ct-status">
              <span className="ct-pulse" />
              {callStatus}
            </p>
          </div>
        </div>

        <div className="ct-divider" />

        <div className="ct-actions">
          <button className="ct-btn ct-btn-decline" onClick={handleDecline}>
            <i className="ri-close-fill" style={{ fontSize: 13 }} />
            {inComming ? "Decline" : "End"}
          </button>

          {inComming ? (
            <button className="ct-btn ct-btn-confirm" onClick={handleAnswer}>
              <i className={`ri-${isVideo ? "vidicon" : "phone"}-fill`} style={{ fontSize: 13 }} />
              Answer
            </button>
          ) : (
            <button
              className="ct-btn ct-btn-confirm"
              onClick={() => handleCall(isVideo ? "video" : "audio")}
            >
              <i className={`ri-${isVideo ? "vidicon" : "phone"}-fill`} style={{ fontSize: 13 }} />
              {isVideo ? "Start Video" : "Start Call"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.isVideo === nextProps.isVideo &&
    prevProps.inComming === nextProps.inComming &&
    prevProps.user?._id === nextProps.user?._id &&
    prevProps.currentUser?._id === nextProps.currentUser?._id
  );
});

export default CallModal;